import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Download, Plus, Search, AtSign, Phone, Ban, Trash2, Send, Users, Tag, X } from 'lucide-react';
import { collapseTagCounts, mergeCanonicalTags, tagsEqual } from '../utils/tagUtils';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250];

const PROFILE_FLAGS = [
  ['tradeitDayRegistered', 'TradeIT Day'],
  ['residencyApplied', 'Applied'],
  ['admissionsCallBooked', 'Call booked'],
  ['residencyEnrolled', 'Enrolled'],
  ['societyMember', 'Society'],
  ['trndlineUser', 'Trndline']
];

const contactSources = (contact) => {
  if (Array.isArray(contact.sources) && contact.sources.length) return contact.sources;
  return contact.source ? [contact.source] : [];
};

const marketingLabel = (contact) => {
  if (contact.emailMarketingStatus === 'suppressed' || contact.status === 'suppressed') return 'Suppressed';
  if (contact.emailMarketingStatus === 'unsubscribed') return 'Unsubscribed';
  if (contact.tradeitDayRegistered) return 'Registered';
  return 'Not registered';
};

const cleanLabel = (value) => {
  const text = String(value ?? '').trim();
  if (!text || text.toLowerCase() === 'null' || text.toLowerCase() === 'undefined') return '';
  return text;
};

const nameLooksLikeAPerson = (value) => {
  if (!value) return false;
  if (/^(true|false|unknown)$/i.test(value)) return false;
  if (/^\d+(\.\d+)?$/.test(value)) return false;
  if (/^(cus_|gst-|mber_|user_|card_)/i.test(value)) return false;
  return true;
};

const contactTitle = (contact) => {
  const parts = [cleanLabel(contact.firstName), cleanLabel(contact.lastName)].filter(nameLooksLikeAPerson);
  if (parts.length) return parts.join(' ');
  const email = cleanLabel(contact.email);
  if (email && !/^customer for /i.test(email)) return email;
  return 'No name';
};

const phoneLooksDialable = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length < 10 || digits.length > 15) return false;
  if (digits.length >= 11 && !digits.startsWith('1') && /^20(1[5-9]|2[0-6])/.test(digits)) return false;
  return true;
};

const ContactLabelPills = ({ tags, contactId }) => {
  const cleaned = mergeCanonicalTags(tags || []);
  if (!cleaned.length) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      {cleaned.slice(0, 2).map((tag) => (
        <span
          key={`${contactId}-${tag}`}
          className="inline-flex max-w-[180px] items-center truncate rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700"
          title={tag}
        >
          {tag}
        </span>
      ))}
      {cleaned.length > 2 && (
        <span className="text-xs text-gray-500">+{cleaned.length - 2}</span>
      )}
    </div>
  );
};

const ContactsPage = ({
  contacts,
  contactCounts,
  contactTagCounts,
  contactTagFilter,
  onTagFilterChange,
  contactsTotal,
  contactsFilteredTotal,
  searchTerm,
  onSearchTermChange,
  contactFilter,
  onContactFilterChange,
  contactsPage,
  contactsPageSize,
  contactsTotalPages,
  onPageChange,
  onPageSizeChange,
  contactsLoading,
  selectingAllContacts,
  onSelectAllFiltered,
  onClearSelection,
  selectedContacts,
  onToggleContact,
  onToggleSelectAll,
  exportContacts,
  addToSuppression,
  deleteContact,
  deleteAllContacts,
  setCurrentPage
}) => {
  const counts = contactCounts || { total: 0, email: 0, sms: 0, active: 0, suppressed: 0 };
  const tagCounts = useMemo(
    () => collapseTagCounts(Array.isArray(contactTagCounts) ? contactTagCounts : []),
    [contactTagCounts]
  );
  const selectAllRef = useRef(null);
  const [isDeleteAllModalOpen, setIsDeleteAllModalOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [tagSearchTerm, setTagSearchTerm] = useState('');
  const [tagSortMode, setTagSortMode] = useState('count');

  const hasSelection = selectedContacts.size > 0;

  const filterOptions = [
    { id: 'all', label: 'All Contacts', count: counts.total },
    { id: 'email', label: 'Email Only', count: counts.email },
    { id: 'sms', label: 'SMS Only', count: counts.sms },
    { id: 'active', label: 'Active', count: counts.active },
    { id: 'suppressed', label: 'Suppressed', count: counts.suppressed }
  ];

  const filteredTagCounts = useMemo(() => {
    const entries = tagCounts.filter(({ tag }) =>
      tagSearchTerm ? tag.toLowerCase().includes(tagSearchTerm.toLowerCase()) : true
    );
    if (tagSortMode === 'alpha') {
      return [...entries].sort((a, b) => a.tag.localeCompare(b.tag));
    }
    return [...entries].sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.tag.localeCompare(b.tag);
    });
  }, [tagCounts, tagSearchTerm, tagSortMode]);

  const selectedTag = useMemo(
    () => tagCounts.find((entry) => tagsEqual(entry.tag, contactTagFilter)) || null,
    [tagCounts, contactTagFilter]
  );

  const allSelectedOnPage = contacts.length > 0 && contacts.every((contact) => selectedContacts.has(contact.id));
  const anySelectedOnPage = contacts.some((contact) => selectedContacts.has(contact.id));
  const isDeleteConfirmValid = deleteConfirmation.trim() === 'DELETE';

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = anySelectedOnPage && !allSelectedOnPage;
    }
  }, [anySelectedOnPage, allSelectedOnPage]);

  const startIndex = contactsFilteredTotal === 0 ? 0 : (contactsPage - 1) * contactsPageSize + 1;
  const endIndex = contactsFilteredTotal === 0 ? 0 : Math.min(contactsFilteredTotal, startIndex + contacts.length - 1);

  const handleConfirmDeleteAll = async () => {
    const success = await deleteAllContacts(deleteConfirmation.trim());
    if (success) {
      setIsDeleteAllModalOpen(false);
      setDeleteConfirmation('');
    }
  };

  const closeDeleteModal = () => {
    setIsDeleteAllModalOpen(false);
    setDeleteConfirmation('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Contacts</h1>
          <p className="text-gray-600 mt-1">
            Manage {contactsTotal.toLocaleString()} total contacts
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setIsDeleteAllModalOpen(true)}
            disabled={contactsTotal === 0}
            className="px-4 py-2 bg-white border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Trash2 size={18} />
            Delete All
          </button>
          <button
            onClick={exportContacts}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-300 flex items-center gap-2"
          >
            <Download size={18} />
            Export CSV
          </button>
          <button
            onClick={() => setCurrentPage('upload')}
            className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all duration-300 flex items-center gap-2"
          >
            <Plus size={18} />
            Import
          </button>
        </div>
      </div>

      {/* Filter Pills */}
      <div className="flex flex-wrap gap-2">
        {filterOptions.map((filter) => (
          <button
            key={filter.id}
            onClick={() => onContactFilterChange(filter.id)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
              contactFilter === filter.id
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                : 'bg-white text-gray-700 border border-gray-300 hover:border-indigo-400'
            }`}
          >
            {filter.label} ({filter.count.toLocaleString()})
          </button>
        ))}
      </div>

      {/* Selection notice */}
      {hasSelection && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
          <div>
            <span className="font-semibold">{selectedContacts.size.toLocaleString()}</span> contacts selected.
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {selectedContacts.size < contactsFilteredTotal && (
              <button
                onClick={onSelectAllFiltered}
                disabled={selectingAllContacts}
                className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {selectingAllContacts
                  ? 'Selecting…'
                  : `Select all ${contactsFilteredTotal.toLocaleString()} contacts`}
              </button>
            )}
            <button
              onClick={onClearSelection}
              className="px-3 py-1.5 rounded-lg border border-indigo-400 text-indigo-700 font-semibold hover:bg-white transition-colors duration-200"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Label filter */}
      {tagCounts.length > 0 && (
        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
            <div className="flex-1 min-w-0">
              <label htmlFor="tag-search" className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-gray-700">
                <Tag size={14} className="text-indigo-500" />
                Filter by label
              </label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  id="tag-search"
                  type="text"
                  value={tagSearchTerm}
                  onChange={(e) => setTagSearchTerm(e.target.value)}
                  placeholder="Search labels…"
                  className="w-full rounded-xl border border-gray-300 py-2.5 pl-9 pr-3 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div>
              <label htmlFor="tag-sort" className="mb-1.5 block text-sm font-medium text-gray-700">
                Sort
              </label>
              <select
                id="tag-sort"
                value={tagSortMode}
                onChange={(e) => setTagSortMode(e.target.value)}
                className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="count">Most used</option>
                <option value="alpha">A → Z</option>
              </select>
            </div>
            {contactTagFilter && (
              <button
                type="button"
                onClick={() => onTagFilterChange('')}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-gray-300 px-3 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                <X size={14} />
                Clear label
              </button>
            )}
          </div>

          {selectedTag && (
            <div className="mt-3">
              <span className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white">
                {selectedTag.tag}
                <span className="text-indigo-100">
                  ({selectedTag.count.toLocaleString()})
                </span>
                <button
                  type="button"
                  onClick={() => onTagFilterChange('')}
                  className="rounded-full p-0.5 hover:bg-indigo-500"
                  aria-label="Clear label filter"
                >
                  <X size={14} />
                </button>
              </span>
            </div>
          )}

          <div className="mt-3 max-h-52 overflow-y-auto rounded-xl border border-gray-100">
            <button
              type="button"
              onClick={() => onTagFilterChange('')}
              className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm ${
                !contactTagFilter
                  ? 'bg-indigo-50 font-semibold text-indigo-800'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span>All labels</span>
              <span className="text-xs text-gray-500">{contactsTotal.toLocaleString()}</span>
            </button>
            {filteredTagCounts.length === 0 && (
              <div className="px-4 py-3 text-sm text-gray-500">
                No labels match “{tagSearchTerm}”.
              </div>
            )}
            {filteredTagCounts.map(({ tag, count }) => {
              const isActive = tagsEqual(contactTagFilter, tag);
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() => onTagFilterChange(isActive ? '' : tag)}
                  className={`flex w-full items-center justify-between border-t border-gray-100 px-4 py-2.5 text-left text-sm ${
                    isActive
                      ? 'bg-indigo-50 font-semibold text-indigo-800'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="truncate pr-3">{tag}</span>
                  <span className="shrink-0 text-xs text-gray-500">{count.toLocaleString()}</span>
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-xs text-gray-500">
            {tagCounts.length.toLocaleString()} unique {tagCounts.length === 1 ? 'label' : 'labels'}
            {tagSearchTerm ? ` · ${filteredTagCounts.length} matching` : ''}
          </p>
        </div>
      )}

      {/* Contacts Table */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
        {/* Search Bar */}
        <div className="p-6 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => onSearchTermChange(e.target.value)}
              placeholder="Search by email, phone, name, or company..."
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    className="rounded"
                    checked={allSelectedOnPage}
                    onChange={(e) => onToggleSelectAll(contacts, e.target.checked)}
                  />
                </th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Contact Info</th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Source</th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Status</th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {contactsLoading && (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-gray-500">
                    <div className="flex items-center justify-center gap-3">
                      <div className="h-5 w-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                      <span>Loading contacts...</span>
                    </div>
                  </td>
                </tr>
              )}

              {!contactsLoading && contacts.map((contact) => (
                <tr key={contact.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200">
                  <td className="py-4 px-6">
                    <input
                      type="checkbox"
                      className="rounded"
                      checked={selectedContacts.has(contact.id)}
                      onChange={(e) => onToggleContact(contact, e.target.checked)}
                    />
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 ${contact.email ? 'bg-indigo-100' : 'bg-purple-100'} rounded-lg`}>
                        {contact.email
                          ? <AtSign className="text-indigo-600" size={16} />
                          : <Phone className="text-purple-600" size={16} />}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">
                          {contactTitle(contact)}
                        </div>
                        {contact.email && contactTitle(contact) !== contact.email && (
                          <div className="text-sm text-gray-600">{contact.email}</div>
                        )}
                        {phoneLooksDialable(contact.phone) && (
                          <div className="text-sm text-gray-500">{contact.phone}</div>
                        )}
                        <ContactLabelPills tags={contact.tags} contactId={contact.id} />
                        {contact.company && <div className="text-xs text-gray-500">{contact.company}</div>}
                        {PROFILE_FLAGS.some(([key]) => contact[key]) && (
                          <div className="mt-1 text-xs text-gray-500">
                            {PROFILE_FLAGS.filter(([key]) => contact[key]).map(([, label]) => label).join(' · ')}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <div className="text-sm text-gray-600">{contactSources(contact).join(', ') || '—'}</div>
                    <div className="text-xs text-gray-500">{new Date(contact.createdAt).toLocaleDateString()}</div>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                      marketingLabel(contact) === 'Registered'
                        ? 'bg-indigo-100 text-indigo-800'
                        : marketingLabel(contact) === 'Not registered'
                          ? 'bg-green-100 text-green-800'
                          : marketingLabel(contact) === 'Unsubscribed'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-red-100 text-red-800'
                    }`}>
                      {marketingLabel(contact)}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex gap-2">
                      {contact.status === 'active' && (
                        <button
                          onClick={() => addToSuppression(contact, 'manual')}
                          className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors duration-200"
                          title="Add to suppression list"
                        >
                          <Ban size={16} />
                        </button>
                      )}
                      <button
                        onClick={() => deleteContact(contact.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {!contactsLoading && contacts.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-gray-500">
                    <Users className="mx-auto mb-3 text-gray-400" size={48} />
                    <p className="text-lg font-semibold mb-1">No contacts found</p>
                    <p className="text-sm">Try adjusting your filters or upload new contacts</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-t border-gray-200 px-6 py-4">
          <div className="text-sm text-gray-600">
            {contactsFilteredTotal > 0
              ? `Showing ${startIndex.toLocaleString()}–${endIndex.toLocaleString()} of ${contactsFilteredTotal.toLocaleString()} contacts${contactsFilteredTotal !== contactsTotal ? ` (${contactsTotal.toLocaleString()} total)` : ''}`
              : 'No contacts to display'}
          </div>
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Rows per page:</span>
              <select
                value={contactsPageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onPageChange(Math.max(1, contactsPage - 1))}
                disabled={contactsPage <= 1}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {contactsPage} of {contactsTotalPages.toLocaleString()}
              </span>
              <button
                onClick={() => onPageChange(Math.min(contactsTotalPages, contactsPage + 1))}
                disabled={contactsPage >= contactsTotalPages}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Action Button */}
      {selectedContacts.size > 0 && (
        <div className="fixed bottom-6 right-6 bg-white rounded-2xl shadow-2xl p-6 border-2 border-indigo-500 z-50 animate-[slideUp_0.3s_ease-out]">
          <div className="flex items-center gap-4">
            <div className="text-sm">
              <div className="font-bold text-gray-900 text-lg">{selectedContacts.size.toLocaleString()} selected</div>
              <div className="text-gray-600">Ready to create campaign</div>
            </div>
            <button
              onClick={() => setCurrentPage('campaigns')}
              className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 flex items-center gap-2"
            >
              <Send size={18} />
              Create Campaign
            </button>
          </div>
        </div>
      )}

      {/* Delete All Modal */}
      {isDeleteAllModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-6 space-y-4">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Confirm Bulk Deletion</h3>
              <p className="mt-2 text-sm text-gray-600">
                This will permanently delete all contacts in your workspace. Type <span className="font-semibold text-red-600">DELETE</span> to confirm.
              </p>
            </div>
            <input
              type="text"
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              placeholder="Type DELETE to confirm"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={closeDeleteModal}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeleteAll}
                disabled={!isDeleteConfirmValid}
                className="px-4 py-2 rounded-lg bg-red-600 text-white font-semibold shadow hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Delete Everything
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsPage;
